import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  updateNodeChartTitle,
  updateNodeChartVisibility,
  updateNodeFormTitle,
  updateNodeValueLabel,
  updateNodeValueVisibility
} from '../../api';
import useNodeCatalog, { invalidateNodeCatalog } from '../../hooks/useNodeCatalog';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import LabelField from './LabelField';
import { buildVisibilityGroups, initialVisibility } from './nodeChartVisibility';
import {
  checklistForms,
  countHiddenValues,
  countRenamedValues,
  initialHiddenValues,
  initialLabelOverrides,
  isValueHidden,
  labelFieldKey,
  normalizeLabelInput,
  toStoredLabel,
  valueLabelRows,
  withValueVisibility
} from './nodeChartLabels';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/**
 * Состав графиков дашборда и их названия: что продуцирует загруженный пакет узлов, что из этого
 * показывать и как подписывать.
 *
 * Список целиком приходит из каталога, включая карточки, скрытые по умолчанию, — иначе включить
 * их обратно было бы нечем: эвристика помечает скрытыми все текстовые выходы, и секция
 * «Цитаты и содержание» из-за этого не показывалась никогда.
 *
 * Переименования меняют только показ: пакет узлов, промпты и записанные результаты анализа
 * остаются как есть, а сервер подставляет новые подписи на графиках, в фильтрах, журнале
 * звонков, «Жалобах», «Сотрудниках» и Excel-выгрузках.
 */
const NodeChartVisibilityCard = () => {
  const { toast } = useToast();
  const { catalog, isLoading, isActive } = useNodeCatalog();

  // Локальное оптимистичное состояние. Каталог после записи не перезапрашиваем: он кэшируется
  // модулем, мы его намеренно инвалидируем, и повторное чтение здесь дало бы лишний запрос,
  // мигание списка и гонку с ещё не завершённой записью.
  const [visible, setVisible] = useState(null);
  const [pending, setPending] = useState({});

  // Переименования: сохранённое на сервере, введённое в поля и поля, ждущие ответа сервера.
  const [savedLabels, setSavedLabels] = useState({});
  const [drafts, setDrafts] = useState({});
  const [labelPending, setLabelPending] = useState({});
  const [expanded, setExpanded] = useState({});

  // Скрытые с графика значения: id карточки → коды. Ключ ожидания — тот же, что у поля подписи.
  const [hiddenValues, setHiddenValues] = useState({});
  const [valuePending, setValuePending] = useState({});

  const charts = catalog?.charts;

  useEffect(() => {
    if (!isLoading && charts) {
      setVisible(initialVisibility(charts));
    }
  }, [isLoading, charts]);

  useEffect(() => {
    if (!isLoading && catalog) {
      const overrides = initialLabelOverrides(catalog);
      setSavedLabels(overrides);
      setDrafts(overrides);
      setHiddenValues(initialHiddenValues(catalog));
    }
  }, [isLoading, catalog]);

  const groups = useMemo(() => buildVisibilityGroups(charts ?? []), [charts]);
  const forms = useMemo(() => checklistForms(catalog), [catalog]);

  const handleToggle = async (chartId, next) => {
    setVisible((current) => ({ ...current, [chartId]: next }));
    setPending((current) => ({ ...current, [chartId]: true }));

    try {
      await updateNodeChartVisibility(chartId, next);
      // Без сброса кэша дашборд до перезагрузки вкладки рисовал бы прежний состав — ровно та
      // поломка, что уже была при активации пакета.
      invalidateNodeCatalog();
    } catch (error) {
      setVisible((current) => ({ ...current, [chartId]: !next }));
      toast({
        title: 'Не удалось сохранить состав графиков',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setPending((current) => ({ ...current, [chartId]: false }));
    }
  };

  /** Галка «показывать на графике» у значения — оптимистично, с откатом, как у карточек. */
  const handleValueToggle = async (chartId, code, visible) => {
    const key = labelFieldKey.value(chartId, code);
    setHiddenValues((current) => withValueVisibility(current, chartId, code, visible));
    setValuePending((current) => ({ ...current, [key]: true }));

    try {
      await updateNodeValueVisibility(chartId, code, visible);
      // Дашборд берёт скрытые значения из каталога: без сброса кэша он добирал бы скрытый код
      // нулевой строкой до перезагрузки страницы.
      invalidateNodeCatalog();
    } catch (error) {
      setHiddenValues((current) => withValueVisibility(current, chartId, code, !visible));
      toast({
        title: 'Не удалось сохранить показ значения',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setValuePending((current) => ({ ...current, [key]: false }));
    }
  };

  const setDraft = (key, value) => setDrafts((current) => ({ ...current, [key]: value }));

  /**
   * Сохраняет одно поле. Запрос уходит, только если значение действительно изменилось: уход
   * фокуса с нетронутого поля не должен дёргать сервер.
   */
  const saveLabel = async (key, rawValue, defaultLabel, request) => {
    const previous = savedLabels[key] ?? '';
    const next = toStoredLabel(rawValue, defaultLabel);

    if (next === normalizeLabelInput(previous)) {
      setDraft(key, previous);
      return;
    }

    setLabelPending((current) => ({ ...current, [key]: true }));

    try {
      await request(next);
      setSavedLabels((current) => ({ ...current, [key]: next ?? '' }));
      setDraft(key, next ?? '');
      // Дашборд, фильтры и журнал звонков берут подписи из каталога и серверных ответов —
      // без сброса кэша новое название появилось бы только после перезагрузки страницы.
      invalidateNodeCatalog();
    } catch (error) {
      setDraft(key, previous);
      toast({
        title: 'Не удалось сохранить название',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setLabelPending((current) => ({ ...current, [key]: false }));
    }
  };

  const renderLabelField = ({ key, id, trackId, defaultLabel, ariaLabel, request }) => (
    <LabelField
      id={id}
      trackId={trackId}
      value={drafts[key] ?? ''}
      placeholder={defaultLabel}
      pending={labelPending[key] === true}
      renamed={normalizeLabelInput(savedLabels[key]) !== null}
      ariaLabel={ariaLabel}
      resetTitle="Вернуть название из пакета"
      onChange={(value) => setDraft(key, value)}
      onCommit={() => saveLabel(key, drafts[key], defaultLabel, request)}
      onRevert={() => setDraft(key, savedLabels[key] ?? '')}
      onReset={() => saveLabel(key, '', defaultLabel, request)}
    />
  );

  const renderValueLabels = (chart) => {
    const rows = valueLabelRows(chart);
    if (rows.length === 0) {
      return null;
    }

    const isExpanded = expanded[chart.id] === true;
    const renamedCount = countRenamedValues(chart, savedLabels);
    const hiddenCount = countHiddenValues(chart, hiddenValues);
    // Галки только там, где сервер набор применяет: у воронки скрытый этап исказил бы
    // накопительные цифры, у чек-листа и флагов значения звонки не делят.
    const canHide = chart.canHideValues === true;
    const Icon = isExpanded ? ChevronDown : ChevronRight;

    return (
      <div className="space-y-2">
        <Button
          type="button"
          data-ev="settings.node_charts.values_expand"
          variant="ghost"
          size="sm"
          className="h-7 px-1 text-xs text-muted-foreground"
          aria-expanded={isExpanded}
          onClick={() => setExpanded((current) => ({ ...current, [chart.id]: !isExpanded }))}
        >
          <Icon className="mr-1 h-4 w-4" />
          {canHide ? 'Подписи и показ значений' : 'Подписи значений'} ({rows.length})
          {renamedCount > 0 && ` · переименовано ${renamedCount}`}
          {hiddenCount > 0 && ` · скрыто ${hiddenCount}`}
        </Button>
        {isExpanded && (
          <div className="space-y-3 sm:space-y-2 sm:pl-5">
            {canHide && (
              <p className="text-xs text-muted-foreground">
                Снятая галка убирает значение с графика и из «применимых звонков» — доли остальных
                пересчитываются. В фильтрах, журнале звонков и выгрузках значение остаётся.
              </p>
            )}
            {rows.map((row) => {
              const key = labelFieldKey.value(chart.id, row.code);
              const hidden = canHide && isValueHidden(hiddenValues, chart.id, row.code);

              return (
                <div
                  key={row.code}
                  data-value-code={row.code}
                  className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)] sm:items-center sm:gap-3"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {canHide && (
                      <Checkbox
                        id={`value-visible-${chart.id}-${row.code}`}
                        data-ev="settings.node_charts.value_visibility_toggle"
                        aria-label={`Показывать значение ${row.code} на графике`}
                        title="Показывать на графике"
                        checked={!hidden}
                        disabled={valuePending[key] === true}
                        onCheckedChange={(value) => handleValueToggle(chart.id, row.code, value === true)}
                      />
                    )}
                    <span className="break-all font-mono text-xs text-muted-foreground">{row.code}</span>
                  </div>
                  <span className={`text-sm text-muted-foreground${hidden ? ' line-through' : ''}`}>
                    {row.defaultLabel}
                  </span>
                  {renderLabelField({
                    key,
                    id: `value-label-${chart.id}-${row.code}`,
                    trackId: 'settings.node_charts.value_label_edit',
                    defaultLabel: row.defaultLabel,
                    ariaLabel: `Подпись значения ${row.code}`,
                    request: (label) => updateNodeValueLabel(chart.id, row.code, label)
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (!isActive || visible === null || groups.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Состав и названия графиков</CardTitle>
        <CardDescription>
          Какие карточки строит загруженный пакет узлов, на какой вкладке они появляются и как
          называются. Названия графиков и подписи значений можно поменять, а отдельные значения —
          убрать с графика. Это касается только отображения: сам пакет и результаты анализа не
          меняются, а пустое поле возвращает название из пакета. Настройка общая для всей
          инсталляции, а не персональная. Выбор сохраняется сразу и переживает перезаливку пакета.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {groups.map((tab) => (
          <div key={tab.id} className="space-y-3">
            <h4 className="text-sm font-semibold">{tab.title}</h4>
            {tab.sections.map((section) => (
              <div key={section.id} className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {section.title}
                </p>
                <div className="space-y-2">
                  {section.charts.map((chart) => {
                    const titleKey = labelFieldKey.chart(chart.id);
                    const defaultTitle = chart.defaultTitle ?? chart.title;
                    const isRenamed = normalizeLabelInput(savedLabels[titleKey]) !== null;

                    return (
                      // data-chart-row, а не data-chart-id: тот уже стоит на карточке порядка
                      // пунктов этой же вкладки, и опора e2e перестала бы быть однозначной.
                      <div key={chart.id} data-chart-row={chart.id} className="flex items-start gap-3 rounded-md border p-3">
                        {/* Radix-чекбокс это button, поэтому подпись связываем через htmlFor,
                            а не оборачиванием в <label>. */}
                        <Checkbox
                          id={`chart-${chart.id}`}
                          data-ev="settings.node_charts.chart_visibility_toggle"
                          className="mt-0.5"
                          checked={visible[chart.id] === true}
                          disabled={pending[chart.id] === true}
                          onCheckedChange={(value) => handleToggle(chart.id, value === true)}
                        />
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="space-y-1">
                            <Label htmlFor={`chart-${chart.id}`} className="cursor-pointer">
                              {isRenamed ? savedLabels[titleKey] : defaultTitle}
                            </Label>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="break-all font-mono text-xs text-muted-foreground">{chart.id}</span>
                              <Badge variant="outline" className="font-normal">
                                {chart.kind}
                              </Badge>
                              {chart.hiddenByDefault && (
                                <Badge variant="secondary" className="font-normal">
                                  скрыт по умолчанию
                                </Badge>
                              )}
                              {isRenamed && (
                                <span className="text-xs text-muted-foreground">
                                  в пакете: {defaultTitle}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="grid gap-1 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center sm:gap-3">
                            <Label
                              htmlFor={`chart-title-${chart.id}`}
                              className="text-xs font-normal text-muted-foreground"
                            >
                              Название
                            </Label>
                            {renderLabelField({
                              key: titleKey,
                              id: `chart-title-${chart.id}`,
                              trackId: 'settings.node_charts.title_edit',
                              defaultLabel: defaultTitle,
                              ariaLabel: `Название графика ${chart.id}`,
                              request: (title) => updateNodeChartTitle(chart.id, title)
                            })}
                          </div>
                          {renderValueLabels(chart)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}

        {forms.length > 0 && (
          <div className="space-y-3">
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">Формы чек-листа</h4>
              <p className="text-xs text-muted-foreground">
                Название формы в селекторе «Форма оценки» на вкладке «Сотрудники» и в карточке
                звонка. На названия графиков не влияет — их переименовывают выше.
              </p>
            </div>
            <div className="space-y-2">
              {forms.map((node) => {
                const defaultTitle = node.defaultTitle ?? node.title;

                return (
                  <div
                    key={node.nodeId}
                    className="grid gap-2 rounded-md border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] sm:items-center sm:gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm">{defaultTitle}</p>
                      <span className="break-all font-mono text-xs text-muted-foreground">{node.nodeId}</span>
                    </div>
                    {renderLabelField({
                      key: labelFieldKey.form(node.nodeId),
                      id: `form-title-${node.nodeId}`,
                      trackId: 'settings.node_charts.form_title_edit',
                      defaultLabel: defaultTitle,
                      ariaLabel: `Название формы ${node.nodeId}`,
                      request: (title) => updateNodeFormTitle(node.nodeId, title)
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NodeChartVisibilityCard;
